import { Injectable, inject } from '@angular/core';
import { getVriSectorColor } from '@cropx-web/components/map/map-layers/vri-layer/vri-layer.component';
import { FieldVraSelectors } from '@cropx-web/screens/field/state/field-vra.state';
import { FieldSelectors } from '@cropx-web/screens/field/state/field.state';
import { ApiGatewayService } from '@cropx-web/services/api-gateway/ApiGatewayService';
import { ManagementZones } from '@cropx-web/services/api-gateway/MachineDataApiService';
import { ApiService } from '@cropx-web/services/network/ApiService';
import {
  CropSoilZone,
  CropSoilZoneRecommendation,
  FieldIrrigationPrescription,
  FieldIrrigationRecommendation,
} from '@cropx-web/services/network/endpoints/FieldApiModels';
import { TranslateService } from '@ngx-translate/core';
import {
  Action,
  Selector,
  State,
  StateContext,
  Store,
  createPropertySelectors,
} from '@ngxs/store';
import { produce } from 'immer';
import { EMPTY, Observable, catchError, mergeMap, tap } from 'rxjs';

import { GenerateManagementZonesSelectors } from '../../state/generate-management-zones.state';
import { VriSteps } from './vri-map-steps';

export enum IrrigationMethod {
  FullPointIrrigation = 'FullPointIrrigation',
  OptimizedWaterUptake = 'OptimizedWaterUptake',
}

export enum IrrigationBase {
  Manual = 'Manual',
  Sensor = 'Sensor',
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace CreateVriMap {
  const type = 'CreateVriMap';

  export class SetStep {
    static readonly type = `[${type}] SetStep`;
    constructor(public step: VriSteps) {}
  }

  export class SetName {
    static readonly type = `[${type}] SetName`;
    constructor(public name: string) {}
  }

  export class SetHoverSector {
    static readonly type = `[${type}] SetHoverSector`;
    constructor(public hover: number) {}
  }

  export class SetManagementZones {
    static readonly type = `[${type}] SetManagementZones`;
    constructor(public zones: ManagementZones) {}
  }

  export class FetchCropSoilZones {
    static readonly type = `[${type}] FetchCropSoilZones`;
    constructor(public fieldId: string) {}
  }

  export class ClearCropSoilZones {
    static readonly type = `[${type}] ClearCropSoilZones`;
    constructor() {}
  }
  export class ClearManagementZones {
    static readonly type = `[${type}] ClearManagementZones`;
    constructor() {}
  }

  export class FetchCropSoilZoneRecommendations {
    static readonly type = `[${type}] FetchCropSoilZoneRecommendations`;
    constructor(public fieldId: string) {}
  }

  export class FetchFieldRecommendations {
    static readonly type = `[${type}] FetchFieldRecommendations`;
    constructor(public fieldId: string) {}
  }

  export class SetIrrigationBase {
    static readonly type = `[${type}] SetIrrigationBase`;
    constructor(public base: IrrigationBase) {}
  }

  export class SetMinDistributeValue {
    static readonly type = `[${type}] SetMinDistributeValue`;
    constructor(public value: number) {}
  }

  export class SetMaxDistributeValue {
    static readonly type = `[${type}] SetMaxDistributeValue`;
    constructor(public value: number) {}
  }

  export class SetIrrigationMethod {
    static readonly type = `[${type}] SetIrrigationMethod`;
    constructor(public method: IrrigationMethod) {}
  }

  export class SetZoneWaterValues {
    static readonly type = `[${type}] SetZoneWaterValues`;
    constructor(public values: Record<number, number>) {}
  }

  export class SetZoneWaterValue {
    static readonly type = `[${type}] SetZoneWaterValue`;
    constructor(
      public zone: number,
      public value: number,
    ) {}
  }

  export class SetZoneWaterValuesLocked {
    static readonly type = `[${type}] SetZoneWaterValuesLocked`;
    constructor(public locked: boolean) {}
  }

  export class FetchPrescription {
    static readonly type = `[${type}] FetchPrescription`;
    constructor(
      public fieldId: string,
      public amplificationFactor: number,
      public baseRate: number,
      public direction: string,
      public sectorSize: number,
      public sectors: PrescriptionGeometry[],
    ) {}
  }

  export class SetApplicationValuesFromPrescription {
    static readonly type = `[${type}] SetApplicationValuesFromPrescription`;
    constructor(public prescription: FieldIrrigationPrescription) {}
  }

  export class SetApplicationValue {
    static readonly type = `[${type}] SetApplicationValue`;
    constructor(
      public fromAngle: number,
      public value: number,
    ) {}
  }

  export class SetApplicationValueLocked {
    static readonly type = `[${type}] SetApplicationValueLocked`;
    constructor(
      public fromAngle: number,
      public locked: boolean,
    ) {}
  }

  export class SetValuesLocked {
    static readonly type = `[${type}] SetValuesLocked`;
    constructor(public locked: boolean) {}
  }
}

export type PrescriptionGeometry = {
  applicationDepth: number;
  geometry: GeoJSON.MultiPolygon;
};

export interface CreateVriMapModel {
  step: VriSteps;
  name: string;

  hoverSector: number;

  managementZones: ManagementZones;
  managementZonesLocked: boolean;

  cropSoilZones: CropSoilZone[];
  cropSoilZonesLoading: boolean;

  cropSoilZoneRecommendation: CropSoilZoneRecommendation[];
  cropSoilZoneRecommendationLoading: boolean;

  fieldRecommendation: FieldIrrigationRecommendation;
  fieldRecommendationLoading: boolean;

  irrigationBase: IrrigationBase;
  irrigationMethod: IrrigationMethod;

  minDistributeValue: number;
  maxDistributeValue: number;

  lockedZoneWaterValues: boolean;
  zoneWaterValues: Record<number, number>;

  prescription: FieldIrrigationPrescription;
  prescriptionLoading: boolean;

  applicationValues: Record<
    number,
    { value: number; locked: boolean; speed: number }
  >;
}

@State<CreateVriMapModel>({
  name: 'createVriMap',
  defaults: {
    step: VriSteps.Zones,
    name: null,

    hoverSector: null,

    managementZones: null,
    managementZonesLocked: false,

    cropSoilZones: null,
    cropSoilZonesLoading: false,

    cropSoilZoneRecommendation: null,
    cropSoilZoneRecommendationLoading: false,

    fieldRecommendation: null,
    fieldRecommendationLoading: false,

    irrigationBase: IrrigationBase.Sensor,
    irrigationMethod: IrrigationMethod.FullPointIrrigation,
    minDistributeValue: null,
    maxDistributeValue: null,
    lockedZoneWaterValues: false,
    zoneWaterValues: {},

    prescription: null,
    prescriptionLoading: false,

    applicationValues: {},
  },
})
@Injectable()
export class CreateVriMapState {
  private store = inject(Store);
  private api = inject(ApiGatewayService);
  private oldApi = inject(ApiService);
  private translate = inject(TranslateService);

  @Action(CreateVriMap.SetManagementZones)
  setManagementZones(
    ctx: StateContext<CreateVriMapModel>,
    action: CreateVriMap.SetManagementZones,
  ): void {
    ctx.patchState({
      managementZones: action.zones,
    });
  }

  @Action(CreateVriMap.SetStep)
  setStep(
    ctx: StateContext<CreateVriMapModel>,
    action: CreateVriMap.SetStep,
  ): void {
    ctx.patchState({
      step: action.step,
    });
  }

  @Action(CreateVriMap.SetHoverSector)
  setHoverSector(
    ctx: StateContext<CreateVriMapModel>,
    action: CreateVriMap.SetHoverSector,
  ): void {
    ctx.patchState({
      hoverSector: action.hover,
    });
  }

  @Action(CreateVriMap.SetName)
  setName(
    ctx: StateContext<CreateVriMapModel>,
    action: CreateVriMap.SetName,
  ): void {
    ctx.patchState({
      name: action.name,
    });
  }

  @Action(CreateVriMap.FetchCropSoilZones)
  setCropSoilZones(
    ctx: StateContext<CreateVriMapModel>,
    action: CreateVriMap.FetchCropSoilZones,
  ): Observable<any> {
    ctx.patchState({
      cropSoilZonesLoading: true,
    });
    return this.oldApi.fieldApi.getCropSoilZone(action.fieldId).pipe(
      tap((resp) => {
        ctx.patchState({
          cropSoilZonesLoading: false,
          cropSoilZones: resp.content.data,
        });
      }),
      catchError(() => {
        ctx.patchState({
          cropSoilZonesLoading: false,
          cropSoilZones: null,
        });
        return EMPTY;
      }),
    );
  }

  @Action(CreateVriMap.FetchCropSoilZoneRecommendations)
  fetchCropSoilZoneRecommendations(
    ctx: StateContext<CreateVriMapModel>,
    action: CreateVriMap.FetchCropSoilZoneRecommendations,
  ): Observable<any> {
    ctx.patchState({
      cropSoilZoneRecommendationLoading: true,
    });
    return this.oldApi.fieldApi
      .getCropSoilZoneRecommendations(action.fieldId)
      .pipe(
        tap((resp) => {
          ctx.patchState({
            cropSoilZoneRecommendationLoading: false,
            cropSoilZoneRecommendation: resp.content.data,
          });
        }),
        catchError(() => {
          ctx.patchState({
            cropSoilZoneRecommendationLoading: false,
            cropSoilZoneRecommendation: null,
          });
          return EMPTY;
        }),
      );
  }

  @Action(CreateVriMap.FetchFieldRecommendations)
  fetchFieldRecommendations(
    ctx: StateContext<CreateVriMapModel>,
    action: CreateVriMap.FetchFieldRecommendations,
  ): Observable<any> {
    ctx.patchState({
      fieldRecommendationLoading: true,
    });
    return this.oldApi.fieldApi
      .getFieldIrrigationRecommendations(action.fieldId)
      .pipe(
        tap((resp) => {
          ctx.patchState({
            fieldRecommendationLoading: false,
            fieldRecommendation: resp.content,
          });
        }),
        catchError(() => {
          ctx.patchState({
            fieldRecommendationLoading: false,
            fieldRecommendation: null,
          });
          return EMPTY;
        }),
      );
  }

  @Action(CreateVriMap.ClearCropSoilZones)
  clearCropSoilZones(ctx: StateContext<CreateVriMapModel>): void {
    ctx.patchState({
      cropSoilZones: null,
    });
  }

  @Action(CreateVriMap.ClearManagementZones)
  clearManagementZones(ctx: StateContext<CreateVriMapModel>): void {
    ctx.patchState({
      managementZones: null,
    });
  }

  @Action(CreateVriMap.SetIrrigationBase)
  setIrrigationBase(
    ctx: StateContext<CreateVriMapModel>,
    action: CreateVriMap.SetIrrigationBase,
  ): void {
    ctx.patchState({
      irrigationBase: action.base,
    });
  }

  @Action(CreateVriMap.SetIrrigationMethod)
  setIrrigationMethod(
    ctx: StateContext<CreateVriMapModel>,
    action: CreateVriMap.SetIrrigationMethod,
  ): void {
    ctx.patchState({
      irrigationMethod: action.method,
    });
  }

  @Action(CreateVriMap.SetMinDistributeValue)
  setMinDistributeValue(
    ctx: StateContext<CreateVriMapModel>,
    action: CreateVriMap.SetMinDistributeValue,
  ): void {
    ctx.patchState({
      minDistributeValue: action.value,
    });
  }

  @Action(CreateVriMap.SetMaxDistributeValue)
  setMaxDistributeValue(
    ctx: StateContext<CreateVriMapModel>,
    action: CreateVriMap.SetMaxDistributeValue,
  ): void {
    ctx.patchState({
      maxDistributeValue: action.value,
    });
  }

  @Action(CreateVriMap.SetZoneWaterValuesLocked)
  setZoneWaterValuesLocked(
    ctx: StateContext<CreateVriMapModel>,
    action: CreateVriMap.SetZoneWaterValuesLocked,
  ): void {
    ctx.patchState({
      lockedZoneWaterValues: action.locked,
    });
  }

  @Action(CreateVriMap.SetZoneWaterValues)
  setZoneWaterValues(
    ctx: StateContext<CreateVriMapModel>,
    action: CreateVriMap.SetZoneWaterValues,
  ): void {
    ctx.patchState({
      zoneWaterValues: action.values,
    });
  }

  @Action(CreateVriMap.SetZoneWaterValue)
  setZoneWaterValue(
    ctx: StateContext<CreateVriMapModel>,
    action: CreateVriMap.SetZoneWaterValue,
  ): void {
    ctx.setState(
      produce((state) => {
        state.zoneWaterValues[action.zone] = action.value;
      }),
    );
  }

  @Action(CreateVriMap.SetApplicationValue)
  SetApplicationValue(
    ctx: StateContext<CreateVriMapModel>,
    action: CreateVriMap.SetApplicationValue,
  ): void {
    const fieldId = this.store.selectSnapshot(FieldSelectors.slices.fieldId);
    ctx.setState(
      produce((state) => {
        const current = state.applicationValues[action.fromAngle];
        if (current) {
          current.value = action.value;
        }
      }),
    );

    this.oldApi.fieldApi
      .getIrrigationSectorSpeed(fieldId, action.value || 0)
      .subscribe((resp) => {
        ctx.setState(
          produce((state) => {
            const current = state.applicationValues[action.fromAngle];
            if (current) {
              current.speed = resp.content.speed;
            }
          }),
        );
      });
  }

  @Action(CreateVriMap.SetApplicationValueLocked)
  setApplicationValueLocked(
    ctx: StateContext<CreateVriMapModel>,
    action: CreateVriMap.SetApplicationValueLocked,
  ): void {
    ctx.setState(
      produce((state) => {
        const current = state.applicationValues[action.fromAngle];
        if (current) {
          current.locked = action.locked;
        }
      }),
    );
  }

  @Action(CreateVriMap.SetValuesLocked)
  setValuesLocked(
    ctx: StateContext<CreateVriMapModel>,
    action: CreateVriMap.SetValuesLocked,
  ): void {
    ctx.setState(
      produce((state) => {
        Object.values(state.applicationValues).forEach(
          (av) => (av.locked = action.locked),
        );
      }),
    );
  }

  @Action(CreateVriMap.FetchPrescription)
  fetchPrescription(
    ctx: StateContext<CreateVriMapModel>,
    action: CreateVriMap.FetchPrescription,
  ): Observable<any> {
    ctx.patchState({
      prescriptionLoading: true,
    });

    return this.oldApi.fieldApi
      .getFieldIrrigationPrescription(
        action.fieldId,
        action.amplificationFactor,
        action.baseRate,
        action.direction,
        action.sectorSize,
        action.sectors,
      )
      .pipe(
        tap((resp) => {
          ctx.patchState({
            prescriptionLoading: false,
            prescription: resp.content,
          });
        }),
        mergeMap((resp) => {
          return ctx.dispatch(
            new CreateVriMap.SetApplicationValuesFromPrescription(resp.content),
          );
        }),
        catchError(() => {
          ctx.patchState({
            prescriptionLoading: false,
            prescription: null,
          });
          return EMPTY;
        }),
      );
  }

  @Action(CreateVriMap.SetApplicationValuesFromPrescription)
  setApplicationValuesFromPrescription(
    ctx: StateContext<CreateVriMapModel>,
    action: CreateVriMap.SetApplicationValuesFromPrescription,
  ): void {
    ctx.setState(
      produce((state) => {
        const values: CreateVriMapModel['applicationValues'] =
          action.prescription.sectors.reduce(
            (values, v) => {
              values[v.from] = {
                value: v.applicationDepth,
                speed: v.speed,
                locked: false,
              };
              return values;
            },
            {} as CreateVriMapModel['applicationValues'],
          );

        if (
          Object.keys(state.applicationValues).length !==
          Object.keys(values).length
        ) {
          state.applicationValues = values;
          return;
        }

        Object.keys(values).forEach((newValueKey) => {
          const current: CreateVriMapModel['applicationValues'][0] =
            state.applicationValues[newValueKey];

          if (current && current.locked) {
            values[newValueKey] = current;
          }
        });

        state.applicationValues = values;
      }),
    );
  }
}

export class CreateVriMapSelectors {
  static slices = createPropertySelectors<CreateVriMapModel>(CreateVriMapState);

  @Selector([
    FieldSelectors.slices.geometryLoading,
    CreateVriMapSelectors.slices.cropSoilZonesLoading,
    CreateVriMapSelectors.slices.prescriptionLoading,
    GenerateManagementZonesSelectors.loading,
    FieldVraSelectors.slices.createVriMapLoading,
  ])
  static mapLoading(...loading: boolean[]): boolean {
    return loading.some((l) => l);
  }

  @Selector([
    CreateVriMapSelectors.slices.cropSoilZones,
    CreateVriMapSelectors.slices.managementZones,
  ])
  static useSoilCrops(
    cropSoilZones: CropSoilZone[],
    managementZones: ManagementZones,
  ): boolean {
    return !!cropSoilZones && !managementZones;
  }

  @Selector([
    CreateVriMapSelectors.slices.prescription,
    CreateVriMapSelectors.slices.applicationValues,
  ])
  static prescriptionPerSector(
    prescription: FieldIrrigationPrescription,
    applicationValues: CreateVriMapModel['applicationValues'],
  ): SectorSlice[] {
    const values = Object.values(applicationValues).map((v) => v.value);
    if (!prescription || values.length === 0) {
      return [];
    }
    if (prescription.sectors.length !== values.length) {
      return [];
    }

    const sectors = prescription.sectors.map((s) => {
      const color = getVriSectorColor(values, applicationValues[s.from].value);

      return {
        color: color,
        from: s.from,
        to: s.to,
        application: applicationValues[s.from].value,
        area: s.area,
        speed: applicationValues[s.from].speed,
        locked: applicationValues[s.from].locked,
      };
    });

    return sectors;
  }
}

export type SectorSlice = {
  color: string;
  from: number;
  to: number;
  application: number;
  area: number;
  speed: number;
  locked: boolean;
};
